import { psConf } from "../../conf/adUnitsConf";
import { DigestConf } from "../logic/DigestConf";

class doPrebidJS {
  private isInitialized: boolean = false;
  private logHeader: string = "[PREBID]";
  private adPrebidSlots: any[] = [];
  private executionQueue: Array<() => void> = [];
  private targetingMap: Record<string, any> = {};
  private digest: DigestConf;

  constructor() {
    this.digest = new DigestConf(window);
  }

  private getPbjs() {
    return (window as any).pbjs;
  }

  private ensurePbjsStub(): void {
    const w = window as any;
    if (!w.pbjs) w.pbjs = { que: [] };
    else if (!Array.isArray(w.pbjs.que)) w.pbjs.que = [];
  }

  private get isPbjsReady() {
    const pbjs = this.getPbjs();
    return !!(pbjs && typeof pbjs.requestBids === 'function' && typeof pbjs.setConfig === 'function');
  }

  private scriptSrcFrom(w: any): string {
    const v = this.resolveVersion(w);
    return `https://assets.fanpage.it/ciaoprebid-js/${v}/prebid.js`;
  }

  processQueue() {
    while (this.executionQueue.length > 0) {
      const task = this.executionQueue.shift();
      if (typeof task === "function") task();
    }
  }

  init(wnd: any, callback?: () => void) {
    console.log(`${this.logHeader} Caricamento ciaoPrebid...`);

    if (this.isPbjsReady) {
      console.log(`${this.logHeader} pbjs già pronto`);
      this.isInitialized = true;
      callback?.();
      return;
    }

    const src = this.scriptSrcFrom(wnd);

    if ((window as any).__ciaoprebidLoading) {
      (window as any).__ciaoprebidLoading.then(() => {
        if (this.isPbjsReady) this.isInitialized = true;
        callback?.();
      });
      return;
    }

    (window as any).__ciaoprebidLoading = new Promise<void>((resolve) => {
      const finish = (ready: boolean) => {
        if (ready) {
          this.isInitialized = true;
          (window as any).__ciaoprebidVersion = this.resolveVersion(wnd);
        } else {
          console.warn(`${this.logHeader} continuo senza Prebid (timeout/errore)`);
        }
        resolve();
        callback?.();
      };

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
      if (existing) {
        this.waitForPbjs().then(finish);
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => this.waitForPbjs().then(finish);
      script.onerror = () => {
        console.error(`${this.logHeader} Errore nel caricamento di prebid.js`);
        finish(false);
      };
      document.head.appendChild(script);
    });
  }

  preparePrebidSlots(slots: any) {
    return this.digest.preparePrebidSlots(slots);
  }

  loadPrebidAndExecute(callback: () => void) {
    console.log(`${this.logHeader} Caricamento ciaoPrebid...`);

    if (this.isPbjsReady) {
      this.isInitialized = true;
      this.executionQueue.push(callback);
      this.processQueue();
      return;
    }

    const src = this.scriptSrcFrom(window);

    if ((window as any).__ciaoprebidLoading) {
      (window as any).__ciaoprebidLoading.then(() => {
        if (this.isPbjsReady) this.isInitialized = true;
        this.executionQueue.push(callback);
        this.processQueue();
      });
      return;
    }

    (window as any).__ciaoprebidLoading = new Promise<void>((resolve) => {
      const run = () => {
        if (this.isPbjsReady) this.isInitialized = true;
        this.executionQueue.push(callback);
        this.processQueue();
      };

      const finish = (ready: boolean) => {
        if (ready) (window as any).__ciaoprebidVersion = this.resolveVersion(window);
        else console.warn(`${this.logHeader} continuo senza Prebid (timeout/errore)`);
        resolve();
        run();
      };

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
      if (existing) {
        this.waitForPbjs().then(finish);
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => this.waitForPbjs().then(finish);
      script.onerror = () => {
        console.error(`${this.logHeader} Errore nel caricamento di prebid.js`);
        finish(false);
      };
      document.head.appendChild(script);
    });
  }

  private waitForPbjs(retries = 60, interval = 100): Promise<boolean> {
    this.ensurePbjsStub();
    const w = window as any;

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const done = (ok: boolean) => {
        if (settled) return;
        settled = true;
        resolve(ok);
      };

      try { w.pbjs.que.push(() => done(true)); } catch { /* no-op */ }

      const tick = () => {
        if (this.isPbjsReady) return done(true);
        if (retries-- <= 0) {
          console.error(`${this.logHeader} pbjs non disponibile dopo timeout`);
          return done(false);
        }
        setTimeout(tick, interval);
      };
      tick();
    });
  }

  private isAdUnitArray(input: any): input is Array<{ code: string; bids: any[]; mediaTypes: any }> {
    return (
      Array.isArray(input) &&
      input.length > 0 &&
      typeof input[0]?.code === "string" &&
      Array.isArray(input[0]?.bids) &&
      typeof input[0]?.mediaTypes === "object"
    );
  }

  doPrebid(inputSlots: any, callback: (t: any) => void) {
    console.log(`${this.logHeader} Avvio Prebid...`);

    const executeBids = async () => {
      const adUnits = this.isAdUnitArray(inputSlots) ? inputSlots : await this.preparePrebidSlots(inputSlots);
      const run = () => this.getBidsTargeting(adUnits, callback.bind(this));
      this.isInitialized ? run() : this.loadPrebidAndExecute(run);
    };

    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(() => executeBids(), { timeout: 300 });
    } else {
      setTimeout(() => executeBids(), 150);
    }
  }

  getBidsTargeting(slots: any[], callback: (t: any) => void) {
    return new Promise((resolve) => {
      if (!this.isPbjsReady) {
        console.warn(`${this.logHeader} pbjs non pronto — salto asta, ritorno targeting vuoto`);
        const empty: any = {};
        try { callback?.(empty); } catch { /* no-op */ }
        return resolve(empty);
      }

      this.getPbjs().que.push(() => {
        const pbjs = this.getPbjs();

        pbjs.aliasBidder("pubmatic", "pubmatic_apex", { gvlid: 76 });

        pbjs.setConfig({
          useBidCache: true,
          enableTIDs: true,
          consentManagement: {
            gdpr: {
              cmpApi: "iab",
              timeout: 500,
              actionTimeout: 10000,
              defaultGdprScope: true,
            },
          },
          bidderTimeout: 2000,
          auctionTimeout: 2000,
          timeout: 2200,
          cache: { url: "https://prebid-cache.pbstck.com/cache" },
        });

        pbjs.setConfig({
          currency: {
            adServerCurrency: "EUR",
            conversionRateFile: "https://cdn.jsdelivr.net/gh/prebid/currency-file@1/latest.json",
            conversionRateFileRefreshHours: 1,
            refreshOnLoad: false,
            defaultRates: {
              EUR: { USD: 1.08, GBP: 0.85, CHF: 0.96 },
              USD: { EUR: 0.93, GBP: 0.79, CHF: 0.89 },
            },
          },
          userSync: {
            filterSettings: {
              iframe: { bidders: ["msft", "ix", "rubicon", "pubmatic", "taboola", "connectad"], filter: "include" },
            },
            userIds: [
              {
                name: "pubCommonId",
                storage: { type: "cookie", name: "_pubcid", expires: 365 },
              },
            ],
          },
        });

        pbjs.setConfig({
          userSync: {
            userIds: [{
              name: 'id5Id',
              params: {
                partner: 1727,
                externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js',
                pd: this.getCookie('apat') ? this.getCookie('apat') : '',
                abTesting: {
                  enabled: true,
                  controlGroupPct: 1
                },
                canCookieSync: true,
              },
              storage: {
                type: 'html5',
                name: 'id5id',
                expires: 90,
                refreshInSeconds: 7200
              }
            }],
            auctionDelay: 250
          }
        });

        pbjs.enableAnalytics({
          provider: 'id5Analytics',
          options: {
            partnerId: 1727
          }
        });


        pbjs.setConfig({
          bidderSequence: "fixed",
          auctionOptions: { secondaryBidders: ["pubmatic", "yobee"] },
        });

        this.boostrapPubStack()

        pbjs.setBidderConfig({
          bidders: ["pubmatic_apex"],
          config: {
            schain: {
              validation: "relaxed",
              config: {
                ver: "1.0",
                complete: 1,
                nodes: [{ asi: "publicismedia.it", sid: "12346", hp: 1 }],
              },
            },
          },
        });

        pbjs.setBidderConfig({
          bidders: ["rubicon"],
          config: {
            cache: { url: "https://prebid-server.rubiconproject.com/cache" },
          },
        });


        pbjs.bidderSettings = {
          msft: { storageAllowed: true },
          ix: { storageAllowed: true },
          rubicon: { storageAllowed: true },
          pubmatic: { storageAllowed: true },
          taboola: { storageAllowed: true },
        };

        this.trackTTR();

        const min4high = {
          display: { kv: "display", min: 3.0 },
          video: { kv: "video", min: 5.0 },
        };

        pbjs.setConfig({
          floors: {
            enforcement: { enforceJS: true },
            floorMin: 0.20,
            data: {
              currency: 'EUR',
              schema: { fields: ['mediaType', 'size'] },
              values: {
                'banner|*': 0.20,
                'video|*': 0.20,
                '*|*': 0.20
              }
            }
          }
        });

        pbjs.requestBids({
          adUnits: slots,
          bidsBackHandler: () => {
            console.log(`${this.logHeader} Offerte ricevute`);
            (this as any).adServerTargeting = pbjs.getAdserverTargeting();
            console.log(`[PREBID-ANAL] Risposte dei bidder salvate:`, (this as any).adServerTargeting);

            for (const adUnitCode in (this as any).adServerTargeting) {
              if (this.targetingMap[adUnitCode]) {
                let highest_cpm = 0.0;
                for (const bidder in this.targetingMap[adUnitCode]) {
                  const slotType = adUnitCode.includes("_video") ? "video" : "display";
                  const data = this.targetingMap[adUnitCode][bidder];
                  highest_cpm = parseFloat(`${highest_cpm}`) > parseFloat(`${data.cpm}`) ? highest_cpm : data.cpm;
                  (this as any).adServerTargeting[adUnitCode]["pbj_high_yield"] =
                    highest_cpm >= parseFloat(`${min4high[slotType].min}`) ? min4high[slotType].kv : "no";
                  console.log("[PREBID-ANAL] -------------------------------" + slotType + "-------------------------------");
                  console.log(`[PREBID-ANAL] hb_ttr_${bidder} --> raw ${data.rawTTR.toFixed(0)} passed ${data.ttr.toFixed(0)}`);
                  console.log(`[PREBID-ANAL] high_yield = ${(this as any).adServerTargeting[adUnitCode]["pbj_high_yield"]} highest_cpm ${this.targetingMap[adUnitCode]["cpm"]}`);
                  (this as any).adServerTargeting[adUnitCode][`hb_ttr_${bidder}`] = data.ttr.toFixed(0);
                }
              }
            }

            if (typeof callback === "function") callback((this as any).adServerTargeting);
            slots.forEach((s: any) => pbjs.removeAdUnit(s.code));
            (resolve as any)((this as any).adServerTargeting);
          },
        });
      });
    });
  }


  boostrapPubStack() {

    var pbSConf: psConf | null = this.digest.getPubstackConf()
    if (!pbSConf) {
      console.log('[PREBID-ANAL] pubstack conf not founded')
      return;
    }

    console.log(`[PREBID-ANAL] pubstack conf founded ${pbSConf}`)
    const { tag, siteID } = pbSConf;

    if (tag && typeof document !== "undefined") {
      const toAbs = (u: string) => {
        try {
          return new URL(u, document.baseURI).href;
        } catch {
          return "";
        }
      };
      const wanted = toAbs(tag);

      if (wanted) {
        const scripts = Array.from(document.getElementsByTagName("script"));
        const existsBySrc = scripts.some((s) => {
          try {
            return s.src && toAbs(s.src) === wanted;
          } catch {
            return false;
          }
        });
        const existsByData =
          !!document.querySelector(
            `script[data-pubstack-tag="${(window as any).CSS?.escape ? (window as any).CSS.escape(wanted) : wanted}"]`
          );

        if (!existsBySrc && !existsByData) {
          const sc = document.createElement("script");
          sc.async = true;
          sc.src = wanted;
          sc.dataset.pubstackTag = wanted;
          (document.head || document.body || document.documentElement).appendChild(sc);
        }
      }
    }

    pbjs.setConfig({
      gvlMapping: { pubstack_server: 1408 },

      s2sConfig: {
        accountId: siteID,
        enabled: true,
        adapter: "prebidServer",
        bidders: [
          "pubstack_server",
          "AdmaticYobee",
          "Adform",
          "Criteo",
          "FreeWheel",
          "ImproveDigital",
          "IndexExchange",
          "LoopMe",
          "MagniteRubicon",
          "Ogury",
          "OneTag",
          "Pubmatic",
          "RichAudience",
          "Sharetrough",
          "Seedtag",
          "Spark",
          "TripleLift",
          "Unruly",
          "medianet",
          "themediagrid"],
        endpoint: "https://prebid-server.pbstck.com/openrtb2/auction",
        syncEndpoint: "https://prebid-server.pbstck.com/cookie_sync"
      }
    });

  }

  trackTTR() {
    const requestStartTimes: Record<string, number> = {};
    const pbjs = this.getPbjs();

    pbjs.onEvent("auctionInit", (event: any) => {
      requestStartTimes[event.auctionId] = Date.now();
    });

    pbjs.onEvent("bidResponse", (event: any) => {
      const auctionId = event.auctionId;
      const rawTTR = requestStartTimes[auctionId] ? Date.now() - requestStartTimes[auctionId] : 0;
      let ttr: number;
      if (rawTTR <= 1000) {
        ttr = Math.ceil(rawTTR / 50) * 50;
      } else {
        ttr = Math.ceil((rawTTR - 1000) / 200) * 200 + 1000;
      }
      ttr = Math.min(ttr, 3000);

      if (!this.targetingMap[event.adUnitCode]) this.targetingMap[event.adUnitCode] = {};
      this.targetingMap[event.adUnitCode][event.bidder] = {
        cpm: event.cpm,
        ttr: ttr,
        rawTTR: rawTTR,
      };
    });
  }

  private resolveVersion(w: any): string {
    const v = w?.cp_ads_config?.prebidVendor?.version;
    if (!v) {
      console.error(`${this.logHeader} versione Prebid mancante (cp_ads_config.prebidVendor.version)`);
      throw new Error(`${this.logHeader} versione Prebid mancante (cp_ads_config.prebidVendor.version)`);
    }
    return v;
  }

  private getCookie(name: string): string | null {
    return document.cookie
      .split('; ')
      .find(c => c.startsWith(name + '='))
      ?.split('=')[1]
      ?? null;
  }

}

(window as any).prebidjs = new doPrebidJS();
